import * as d3 from 'd3';

import { IMargins } from '../../../models';
import { infoTextColor } from '../../../../common/constants';

export class VerticalBrush {

    private _brushGroup: d3.Selection<any>;
    private _brush: d3.svg.Brush<any, any, any>;
    private _yScale: d3.scale.Linear<number, number>;
    private _width = 20;
    private _margins: IMargins = {
        marginTop: 10,
        marginBottom: 30,
        marginLeft: 10,
        marginRight: 10
    };
    private _container: d3.Selection<any>;

    private _dispatch: any;
    private _range: [number, number];
    private _step: number;
    private _isDisabled: boolean;

    constructor(container: d3.Selection<any>, width: number, height: number) {
        this._container = container;
        this.init(container, width, height);
        this._dispatch = d3.dispatch('brush', 'brushed');
        this.render();
    }

    private init(container: d3.Selection<any>, width: number, height: number) {
        this._width = width;
        const brushMargins = this._margins;
        this._brushGroup = container.append('g')
            .classed('brush-group', true)
            .attr('transform', `translate(${brushMargins.marginLeft},${brushMargins.marginTop})`);
        this._yScale = d3.scale.linear()
            .range([height - brushMargins.marginBottom - brushMargins.marginTop, 0]);
        this.buildBrush();
        this.render();
    }

    private buildBrush() {
        this._brush = d3.svg.brush()
            .y(this._yScale)
            .on('brush', () => {
                let extent: [number, number] = <[number, number]>this._brush.extent();
                if (this._isLocked) {
                    extent = [this._yScale.domain()[0], this._yScale.invert(this._yScale(extent[1]) + 1)];
                    this._brush.extent(extent);
                }
                this._dispatch.brush(extent);
            })
            .on('brushend', () => {
                const extent = this._brush.extent();
                const domain = this._yScale.domain();
                let dist: number;
                let nStep: number;
                if (extent[0] !== this._range[0]) {
                    const min = domain[0];
                    dist = <number>extent[0] - min;
                    nStep = Math.floor(dist / this._step);
                    extent[0] = min + nStep * this._step;
                }
                if (extent[1] !== this._range[1]) {
                    const max = domain[1];
                    dist = max - <number>extent[1];
                    nStep = Math.ceil(dist / this._step);
                    extent[1] = max - nStep * this._step;
                }

                this._dispatch.brushed(extent);
            });
    }

    width() {
        return this._width + this._margins.marginLeft + this._margins.marginRight;
    }

    resize(width: number, height: number) {
        this._yScale = d3.scale.linear().range([height - this._margins.marginBottom - this._margins.marginTop, 0]);
        this._brush.y(this._yScale);
        this.render();
    }

    dispatch() {
        return this._dispatch;
    }

    setDomain(extent: [number, number]) {
        this._yScale.domain(extent);
        this._step = (extent[1] - extent[0]) / 100;
        this.render();
    }

    setExtent(range: [number, number]) {
        this._range = range;
        this._brush.extent(range);
        this.render();
    }

    isLocked(value: boolean): void {
        if (arguments.length) {
            this._isLocked = value;
        }
    }

    isDisabled(value: boolean): void {
        if (arguments.length) {
            this._isDisabled = value;
            if (value) {
                this.disableBrush(this._brushGroup);
            } else {
                this.enableBrush(this._brushGroup);
            }
        }
    }

    private render() {
        this._brushGroup.call(this._brush);
        this._brushGroup.selectAll('rect')
            .attr('x', 0)
            .attr('width', this._width);
        const background = this._brushGroup.select('.background')
            .style('visibility', 'visible')
            .style('fill', 'lightgray');
        const extent = this._brushGroup.select('.extent')
            .style('fill', infoTextColor);
        if (this._isLocked) {
            background.style('cursor', 'pointer')
                .style('pointer-events', 'none');
            extent.style('cursor', 'pointer')
                .style('pointer-events', 'none');
            this._brushGroup.selectAll('.resize.s')
                .classed('disable', true);
        }
        if (this._isDisabled) {
            this.disableBrush(this._brushGroup);
        }
    }

    private disableBrush(container) {
        container
            .style('pointer-events', 'none');
        container.select('.background')
            .style('cursor', 'pointer');
        container.select('.extent')
            .style('cursor', 'pointer');
        container.selectAll('.resize')
            .style('cursor', 'pointer');
    }

    private enableBrush(container) {
        container.style('pointer-events', 'all');
        container.select('.background')
            .style('cursor', 'crosshair');
        container.select('.extent')
            .style('cursor', 'move');
        container.selectAll('.resize')
            .style('cursor', 'ns-resize');
    }

    private _isLocked: boolean;
}